import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { BRAND_NAME } from '../../config/branding';
import { useAppDispatch } from '../../store';
import { openDisclaimerModal } from '../../store/slices/modalsSlice';
import { getFriendlyAuthError } from '../../lib/firebaseErrors';
import { acceptDisclaimer } from '../../services/api';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: (verificationInfo?: { email: string; verificationSent: boolean }) => void;
  initialError?: string | null;
}

const RegisterModal: React.FC<RegisterModalProps> = ({ isOpen, onClose, onSwitchToLogin, initialError }) => {
  const dispatch = useAppDispatch();
  const { register, registerWithGoogle, loading, error, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [agreedToDisclaimer, setAgreedToDisclaimer] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (initialError) {
      setLocalError(initialError);
      // LoginModal also writes pending_register_error; clear so a later Sign Up open
      // does not replay the message from sessionStorage alone (e.g. AuthStatus path).
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('pending_register_error');
      }
      return;
    }
    if (typeof sessionStorage !== 'undefined') {
      const stashed = sessionStorage.getItem('pending_register_error');
      if (stashed) {
        setLocalError(stashed);
        sessionStorage.removeItem('pending_register_error');
      }
    }
  }, [isOpen, initialError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);

    if (!email || !password || !confirmPassword) {
      setLocalError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setLocalError('Password must be at least 6 characters long');
      return;
    }

    if (!agreedToDisclaimer) {
      setLocalError('You must agree to the Disclaimer to create an account');
      return;
    }

    try {
      const result = await register(email, password);
      // Persist the user's disclaimer acceptance server-side immediately
      // so the post-auth gate at the App level doesn't re-prompt them.
      // Best-effort - the DisclaimerGateModal is the safety net if this
      // POST fails for any reason.
      try {
        await acceptDisclaimer();
      } catch {
        /* gate modal will catch it */
      }
      // Instead of showing success in modal, redirect to login modal with verification info
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setAgreedToDisclaimer(false);
      onClose();
      // Call switch to login with verification info to show the "Thank You" / verification screen
      onSwitchToLogin({ email, verificationSent: result.verificationSent });
    } catch (err: any) {
      const friendlyMessage = getFriendlyAuthError(err?.code, 'Registration failed. Please try again.');
      setLocalError(friendlyMessage);
    }
  };

  const handleGoogleRegister = async () => {
    clearError();
    setLocalError(null);

    if (!agreedToDisclaimer) {
      setLocalError('You must agree to the Disclaimer to create an account');
      return;
    }

    try {
      await registerWithGoogle();
      // Persist disclaimer acceptance server-side. Even if this POST
      // fails, the App-level DisclaimerGateModal will re-prompt - but
      // happy-path users skip that round trip entirely.
      try {
        await acceptDisclaimer();
      } catch {
        /* gate modal will catch it */
      }
      setAgreedToDisclaimer(false);
      onClose();
    } catch (err: any) {
      if (err.code !== 'auth/popup-closed-by-user' && err.code !== 'auth/cancelled-popup-request') {
        const friendlyMessage = getFriendlyAuthError(err?.code, 'Google sign up failed. Please try again.');
        setLocalError(friendlyMessage);
      }
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setAgreedToDisclaimer(false);
    clearError();
    setLocalError(null);
    onClose();
  };

  const displayError = localError || error;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'var(--tv-bg-primary)',
        padding: '32px',
        borderRadius: '12px',
        width: '90%',
        maxWidth: '400px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        border: '1px solid var(--tv-border-primary)'
      }}>
        {/* Branding Header */}
        <div className="text-center mb-4">
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo-app.png`}
            alt={BRAND_NAME}
            className="d-block mx-auto mb-2"
            style={{ width: 48, height: 48, objectFit: 'contain' }}
          />
          <h3 className="text-lg font-semibold text-text-primary mb-0.5">
            {BRAND_NAME}
          </h3>
        </div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-text-primary m-0">
            Sign Up
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--tv-text-secondary)',
              padding: '4px'
            }}
          >
            ×
          </button>
        </div>

            {displayError && (
              <div style={{
                backgroundColor: 'var(--tv-error-bg, rgba(244, 67, 54, 0.1))',
                color: 'var(--tv-error-text, #f44336)',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '0.875rem',
                border: '1px solid var(--tv-error-border, rgba(244, 67, 54, 0.2))'
              }}>
                {displayError}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="reg-email" className="block text-sm font-medium text-text-secondary mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  id="reg-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--tv-border-primary)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: 'var(--tv-bg-secondary)',
                    color: 'var(--tv-text-primary)',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter your email"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="reg-password" className="block text-sm font-medium text-text-secondary mb-1.5">
                  Password
                </label>
                <input
                  id="reg-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--tv-border-primary)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: 'var(--tv-bg-secondary)',
                    color: 'var(--tv-text-primary)',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter your password (min. 6 characters)"
                />
                <div className="text-xs text-text-muted mt-1">
                  Use at least 8 characters with a mix of letters and numbers
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="reg-confirm" className="block text-sm font-medium text-text-secondary mb-1.5">
                  Confirm Password
                </label>
                <input
                  id="reg-confirm"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid var(--tv-border-primary)',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    backgroundColor: 'var(--tv-bg-secondary)',
                    color: 'var(--tv-text-primary)',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Confirm your password"
                />
              </div>

              {/* Disclaimer consent checkbox */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  htmlFor="disclaimer-agree"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    lineHeight: 1.4,
                    color: 'var(--tv-text-secondary)',
                  }}
                >
                  <input
                    type="checkbox"
                    id="disclaimer-agree"
                    checked={agreedToDisclaimer}
                    onChange={(e) => setAgreedToDisclaimer(e.target.checked)}
                    disabled={loading}
                    style={{ marginTop: '2px', cursor: 'pointer', accentColor: 'var(--accent-primary)' }}
                  />
                  <span>
                    I have read and agree to the{' '}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); dispatch(openDisclaimerModal()); }}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: 'var(--accent-primary)',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontSize: 'inherit',
                      }}
                    >
                      Disclaimer
                    </button>.
                    I understand this tool does not provide investment advice and all trading involves risk of loss.
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading || !agreedToDisclaimer}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'var(--accent-primary)',
                  color: 'var(--on_primary)',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: (loading || !agreedToDisclaimer) ? 'not-allowed' : 'pointer',
                  opacity: (loading || !agreedToDisclaimer) ? 0.45 : 1,
                  marginBottom: '16px'
                }}
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '16px'
              }}>
                <div style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'var(--tv-border-primary)'
                }}></div>
                <span style={{
                  padding: '0 16px',
                  fontSize: '0.75rem',
                  color: 'var(--tv-text-secondary)'
                }}>
                  OR
                </span>
                <div style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'var(--tv-border-primary)'
                }}></div>
              </div>

              <button
                type="button"
                onClick={handleGoogleRegister}
                disabled={loading || !agreedToDisclaimer}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: 'white',
                  color: '#333',
                  border: '1px solid var(--tv-border-primary)',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: (loading || !agreedToDisclaimer) ? 'not-allowed' : 'pointer',
                  opacity: (loading || !agreedToDisclaimer) ? 0.45 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '20px'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>

              <div style={{
                textAlign: 'center',
                fontSize: '0.875rem',
                color: 'var(--tv-text-secondary)'
              }}>
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => onSwitchToLogin()}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--accent-primary)',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Sign In
                </button>
              </div>
            </form>
      </div>
    </div>
  );
};

export default RegisterModal;
